import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, Send, Zap, Loader2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin-only Auto News control panel. Silently hides for non-admins (endpoint 403s).
const AutoNewsAdmin = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const r = await fetch(`${API}/news/auto-settings`, { credentials: "include" });
      if (r.ok) setS(await r.json());
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy("save");
    try {
      const r = await fetch(`${API}/news/auto-settings`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch),
      });
      if (r.ok) { setS(await r.json()); toast.success("Auto News settings saved"); }
    } catch { toast.error("Could not save"); } finally { setBusy(""); }
  };

  const runNow = async () => {
    setBusy("run");
    try {
      const r = await fetch(`${API}/news/run-now`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (d.published) toast.success(`Published: ${d.headline}${d.telegram_ok ? " · sent to Telegram" : " · Telegram pending"}`);
      else toast.info(`No publish this run (${d.reason || "nothing important"})`);
    } catch { toast.error("Run failed"); } finally { setBusy(""); }
  };

  if (!s) return null;

  const Toggle = ({ on, onClick, label }) => (
    <button onClick={onClick} className="flex items-center gap-2 text-[12px]" data-testid={`autonews-toggle-${label}`}>
      <span className={`w-9 h-5 rounded-full transition-colors relative ${on ? "bg-[color:var(--ws-blue)]" : "bg-[rgba(120,140,180,0.25)]"}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
      </span>
      <span className="text-[color:var(--ws-muted)]">{label}</span>
    </button>
  );

  return (
    <div className="ws-panel p-4 mb-4 ws-fade-up" data-testid="autonews-admin">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <div className="ws-icon-chip w-8 h-8"><Bot className="w-4 h-4" /></div>
          <div>
            <div className="ws-heading text-[13px] font-bold text-white">Auto News Pipeline</div>
            <div className="text-[11px] text-[color:var(--ws-muted)]">News → Journal → Telegram · admin</div>
          </div>
        </div>

        <Toggle on={s.enabled} label="Auto News" onClick={() => save({ enabled: !s.enabled })} />
        <Toggle on={s.telegram_enabled} label="Telegram" onClick={() => save({ telegram_enabled: !s.telegram_enabled })} />

        <div className="flex items-center gap-2 text-[12px]">
          <span className="text-[color:var(--ws-muted)]">Every</span>
          {[2, 3].map((h) => (
            <button key={h} onClick={() => save({ interval_hours: h })} data-testid={`autonews-interval-${h}`}
              className={`px-2.5 py-1 rounded-md text-[12px] font-semibold ${s.interval_hours === h ? "ws-btn-primary" : "ws-btn-ghost"}`}>{h}h</button>
          ))}
        </div>

        <div className="flex items-center gap-2 text-[12px]">
          <span className="text-[color:var(--ws-muted)]">Min importance</span>
          <input type="range" min="0" max="100" value={s.min_importance}
            onChange={(e) => setS({ ...s, min_importance: Number(e.target.value) })}
            onMouseUp={(e) => save({ min_importance: Number(e.target.value) })}
            data-testid="autonews-threshold" className="w-28 accent-[color:var(--ws-blue)]" />
          <span className="text-white font-semibold w-7">{s.min_importance}</span>
        </div>

        <button onClick={runNow} disabled={!!busy} className="ws-btn-primary px-3 py-1.5 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 ml-auto" data-testid="autonews-run-now">
          {busy === "run" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Run now
        </button>
      </div>
    </div>
  );
};

export default AutoNewsAdmin;
